const parseAiColor = (url) => {
  if (!url) return null;

  const filename = decodeURIComponent(url.split('/').pop().split('?')[0]).toUpperCase();

  if (filename.includes('AI_BLACK')) return 'black';
  if (filename.includes('AI_WHITE')) return 'white';

  return null;
};

const getImageUrl = (image) => image?.currentSrc || image?.src || '';

const setLabelState = (label, color, visible) => {
  if (!label) return;

  label.classList.remove('sc-ai-media-label--visible', 'sc-ai-media-label--hidden');

  if (!visible || !color) {
    label.classList.add('sc-ai-media-label--hidden');
    return;
  }

  label.classList.remove('sc-ai-media-label--black', 'sc-ai-media-label--white');
  label.classList.add(`sc-ai-media-label--${color}`, 'sc-ai-media-label--visible');
};

const getVisibleCardImage = (container) => {
  const images = [...container.querySelectorAll('img')];

  if (images.length === 0) return null;
  if (images.length === 1) return images[0];

  const opaqueImage = images.find((image) => {
    const opacity = window.getComputedStyle(image).opacity;
    return Number(opacity) > 0.5;
  });

  return opaqueImage || images[0];
};

const getActivePictureUrl = (picture) => {
  if (!picture) return '';

  const img = picture.querySelector('img');
  const sources = [...picture.querySelectorAll('source[media]')];

  for (const source of sources) {
    const mediaQuery = window.matchMedia(source.media.trim());

    if (mediaQuery.matches) {
      const sourceUrl = source.srcset?.split(',')[0]?.trim().split(/\s+/)[0];
      if (sourceUrl) return sourceUrl;
    }
  }

  return getImageUrl(img);
};

const getResponsiveMediaUrl = (container) => {
  const desktopSrc = container.dataset.scAiDesktopSrc;
  const mobileSrc = container.dataset.scAiMobileSrc;
  const breakpoint = container.dataset.scAiBreakpoint;

  if (desktopSrc && mobileSrc && breakpoint) {
    const mode = container.dataset.scAiResponsiveMode || 'min';
    const query =
      mode === 'max' ? `(max-width: ${breakpoint}px)` : `(min-width: ${breakpoint}px)`;
    const mediaMatches = window.matchMedia(query).matches;
    const useDesktop = mode === 'min' ? mediaMatches : !mediaMatches;

    return useDesktop ? desktopSrc : mobileSrc;
  }

  const picture = container.querySelector('picture');

  if (picture) return getActivePictureUrl(picture);

  return getActiveContentBlockVideoUrl(container);
};

const getActiveContentBlockVideoUrl = (container) => {
  const videos = [...container.querySelectorAll('video')];

  if (videos.length === 0) return '';

  const visibleVideo = videos.find((video) => {
    const style = window.getComputedStyle(video);
    return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
  });

  const video = visibleVideo || videos[videos.length - 1];
  const source = video?.querySelector('source');

  return source?.src || video?.currentSrc || video?.src || '';
};

class AiMediaController {
  #container;
  #label;
  #observers = [];

  constructor(container) {
    this.#container = container;
    this.#label = container.querySelector('[data-sc-ai-media-label]');

    if (!this.#label) return;

    this.#bindCardMedia();
    this.#bindCarouselSlide();
    this.#bindResponsiveMedia();
  }

  #bindCardMedia() {
    if (!this.#container.matches('[data-sc-ai-media]')) return;

    const images = [...this.#container.querySelectorAll('img')];

    if (images.length === 0) return;

    const updateFromVisibleImage = () => {
      const visibleImage = getVisibleCardImage(this.#container);
      const color = parseAiColor(getImageUrl(visibleImage));
      setLabelState(this.#label, color, Boolean(color));
    };

    updateFromVisibleImage();

    const cardWrapper = this.#container.closest('.card-wrapper');

    if (cardWrapper) {
      cardWrapper.addEventListener('mouseenter', updateFromVisibleImage);
      cardWrapper.addEventListener('mouseleave', updateFromVisibleImage);
    }

    this.#container.addEventListener('transitionend', updateFromVisibleImage);

    const observer = new MutationObserver(updateFromVisibleImage);

    images.forEach((image) => {
      observer.observe(image, {
        attributes: true,
        attributeFilter: ['src', 'srcset'],
      });
    });

    this.#observers.push(observer);
  }

  #bindCarouselSlide() {
    const slide = this.#container.closest('.product__media-item, .slider__slide');

    if (!slide) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const image = this.#container.querySelector('img');
          const color = parseAiColor(getImageUrl(image));

          if (!color) return;

          setLabelState(this.#label, color, entry.isIntersecting && entry.intersectionRatio >= 0.5);
        });
      },
      { threshold: [0, 0.5, 1] }
    );

    observer.observe(slide);
    this.#observers.push(observer);
  }

  #bindResponsiveMedia() {
    if (!this.#container.matches('[data-sc-ai-media-responsive]')) return;

    const updateFromResponsiveMedia = () => {
      const activeUrl = getResponsiveMediaUrl(this.#container);
      const color = parseAiColor(activeUrl);

      setLabelState(this.#label, color, Boolean(color));
    };

    updateFromResponsiveMedia();

    const onBreakpointChange = () => updateFromResponsiveMedia();

    window.addEventListener('resize', onBreakpointChange);

    const breakpoint = this.#container.dataset.scAiBreakpoint;
    const mode = this.#container.dataset.scAiResponsiveMode || 'min';

    if (breakpoint) {
      const query =
        mode === 'max' ? `(max-width: ${breakpoint}px)` : `(min-width: ${breakpoint}px)`;
      const mediaQuery = window.matchMedia(query);

      if (typeof mediaQuery.addEventListener === 'function') {
        mediaQuery.addEventListener('change', onBreakpointChange);
      } else {
        mediaQuery.addListener(onBreakpointChange);
      }
    }

    const observer = new MutationObserver(updateFromResponsiveMedia);

    this.#container.querySelectorAll('video source').forEach((source) => {
      observer.observe(source, {
        attributes: true,
        attributeFilter: ['src'],
      });
    });

    this.#observers.push(observer);
  }

  destroy() {
    this.#observers.forEach((observer) => observer.disconnect());
  }
}

const initAiMediaLabels = (root = document) => {
  root.querySelectorAll('[data-sc-ai-media-label]').forEach((label) => {
    const host = label.closest(
      '[data-sc-ai-media], [data-sc-ai-media-responsive], .product__media.media, .sc-ai-media-label-host, .media'
    );

    if (!host || host.dataset.scAiMediaInitialized === 'true') return;

    host.dataset.scAiMediaInitialized = 'true';
    new AiMediaController(host);
  });
};

if (!customElements.get('ai-media-label')) {
  customElements.define(
    'ai-media-label',
    class AiMediaLabel extends HTMLElement {
      connectedCallback() {
        if (!this.dataset.scAiMediaInitialized) {
          this.dataset.scAiMediaInitialized = 'true';
          new AiMediaController(this);
        }
      }
    }
  );
}

document.addEventListener('DOMContentLoaded', () => initAiMediaLabels());
document.addEventListener('shopify:section:load', (event) => initAiMediaLabels(event.target));
